import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import ProductFAQ from '@/components/landing/ProductFAQ';
import React, { Suspense } from 'react';
import Header from '@/components/landing/product/sub/Header'
import databg from '@/assets/products/earn.png';
import { BarChart3, FileText, LineChart, Calculator, TrendingUp, Users } from 'lucide-react';
import StyledProducts from '../styles/ProductsPageStyles';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import Marquee from '@/components/landing/product/sub/Logos';
import FeaturesGrid from '@/components/landing/product/sub/FeaturesGrid';

const loadGlobeComp = () => import('@/components/landing/product/sub/Globe');
const GlobeComp = React.lazy(loadGlobeComp);
const loadDataGrid = () => import('@/components/landing/product/DataGrid');
const DataGrid = React.lazy(loadDataGrid);
const loadCardList = () => import('@/components/landing/product/CardList');
const CardList = React.lazy(loadCardList);
const loadResponses = () => import('@/components/landing/product/sub/Responses');
const Responses = React.lazy(loadResponses);
const loadDatasetDetails = () => import('@/components/landing/product/main/DatasetDetails');
const DatasetDetails = React.lazy(loadDatasetDetails);
const loadW1 = () => import('@/components/landing/product/main/datasets/earnings');
const W1 = React.lazy(loadW1);
const loadW2 = () => import('@/components/landing/product/main/datasets/earnings-revision-card');
const W2 = React.lazy(loadW2);
const loadW3 = () => import('@/components/landing/product/main/datasets/earnings-whisper-card');
const W3 = React.lazy(loadW3);
const loadW4 = () => import('@/components/landing/product/main/datasets/financial-quality-card');
const W4 = React.lazy(loadW4);
const loadW5 = () => import('@/components/landing/product/main/datasets/financial-bridge-card');
const W5 = React.lazy(loadW5);
const loadOutcomes = () => import('@/components/landing/product/sub/OutcomeStats');
const Outcomes = React.lazy(loadOutcomes);
const loadPricingTable = () => import('@/components/landing/PricingTable');
const PricingTable = React.lazy(loadPricingTable);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);

const EarningsPage: React.FC = () => {
  useIdlePrefetch([loadGlobeComp, loadDataGrid, loadCardList, loadResponses, loadDatasetDetails, loadW1, loadW2, loadW3, loadW4, loadW5, loadOutcomes, loadPricingTable, loadGetStarted, loadFooter]);
  return (
    <>
      <SEOMetadata
        title="Earnings Data API – Estimates, Calls & Transcripts"
        description="Earnings API with EPS actuals, estimates, surprises, call transcripts and 8-K tables for 60,000+ companies, built for backtesting and event-driven trading."
        canonicalUrl="https://axionquant.com/products/earnings"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Axion Earnings Data API",
          "description": "Earnings data API with historical EPS actuals, analyst estimates, surprise percentages, earnings call transcripts, and structured SEC 8-K financial tables for 60,000+ public companies.",
          "brand": { "@type": "Brand", "name": "AxionQuant" },
          "image": "https://axionquant.com/meta.jpg",
          "category": "Earnings Data API",
          "offers": { "@type": "AggregateOffer", "lowPrice": "29", "highPrice": "99", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Free tier available; paid plans from $29/month" }
        })}</script>
      </Helmet>

      <StyledProducts>
<Header
  docs="/docs/03-fundamentals/earnings"
  title="Earnings Data API: Estimates, Calls & Transcripts"
  body="Complete earnings data API - EPS actuals, surprises, forward estimates, earnings call transcripts, and quarterly report detail. Backtest earnings strategies, track estimate revisions, and benchmark individual names against index-level expectations."
  crumb={{name: 'Earnings', path:'/products/earnings'}}
  tabs={[
    {
      label: 'Earnings History',
      language: 'python',
      filename: 'earnings_history.py',
      bg: '16.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

tickers = ["AAPL", "MSFT", "NVDA", "META"]

def fetch_history(row):
    t = row["ticker"]
    return (t, [
        {"ticker": t, "quarter": q["quarter"], "surprise": q["epsDifference"]}
        for q in client.earnings.history(t)
    ])

history_map = {}
utils.work(utils.df([{"ticker": t} for t in tickers]), fetch_history, history_map)

all_rows  = [row for rows in history_map.values() for row in rows]
df_hist   = utils.df(all_rows)

beat_rate = (
    df_hist.groupby("ticker")
    .apply(lambda x: (x["surprise"] > 0).mean())
    .reset_index(name="beat_rate")
)

visualize.barh(beat_rate, x="beat_rate", y="ticker")
`,
    },
    {
      label: 'Forward Estimates',
      language: 'python',
      filename: 'forward_estimates.py',
      bg: '7.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

tickers = ["NVDA", "TSLA", "META", "MSFT"]

def fetch_trend(row):
    t = row["ticker"]
    return (t, [
        {
            "ticker":  t,
            "period":  p["period"],
            "eps_avg": p["earningsEstimate"]["avg"],
            "rev_avg": p["revenueEstimate"]["avg"],
        }
        for p in client.earnings.trend(t)
    ])

trend_map = {}
utils.work(utils.df([{"ticker": t} for t in tickers]), fetch_trend, trend_map)

df_trend = utils.df([r for rows in trend_map.values() for r in rows])
df_near  = df_trend[df_trend["period"].isin(["0q", "+1q"])]

visualize.scatter(df_near, x="eps_avg", y="rev_avg", hover=["ticker", "period"], group="period")
`,
    },
    {
      label: 'Quarterly Report',
      language: 'python',
      filename: 'quarterly_report.py',
      bg: '9.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

ticker   = "NVDA"
quarters = [("2024", "Q1"), ("2024", "Q2"), ("2024", "Q3"), ("2024", "Q4")]

df_reports = utils.df([
    {
        "quarter":        f"{y}-{q}",
        "eps_actual":     r["epsActual"],
        "eps_estimate":   r.get("epsEstimate"),
        "revenue_actual": r["revenueActual"],
        "surprise":       r["epsDifference"],
    }
    for y, q in quarters
    for r in [client.earnings.report(ticker, year=y, quarter=q)]
])

visualize.graph(
    df_reports,
    x="quarter",
    bars=["revenue_actual"],
    lines=["eps_actual", "eps_estimate"],
    title=f"{ticker} - Quarterly Earnings (2024)",
)
`,
    },
    {
      label: 'Index Benchmarking',
      language: 'python',
      filename: 'index_benchmarking.py',
      bg: '5.jpg',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

tickers     = ["NVDA", "AAPL", "MSFT", "TSLA"]
index_trend = client.earnings.index("SPY")

def fetch_estimates(row):
    t     = row["ticker"]
    trend = client.earnings.trend(t)
    return (t, {
        "ticker":    t,
        "eps_next_y": trend[3]["earningsEstimate"]["avg"],
        "rev_next_q": trend[1]["revenueEstimate"]["avg"],
    })

est_map = {}
utils.work(utils.df([{"ticker": t} for t in tickers]), fetch_estimates, est_map)

df_est = utils.df(list(est_map.values()))

visualize.bar(df_est, x="ticker", y="eps_next_y")
`,
    },
  ]}
/>
      <Marquee />

      <FeaturesGrid
        title="Earnings Data API. "
        subtitle="EPS data, earnings estimates, and financial ratios. "
        features={[
        {
          fig: 'FIG 0.1',
          icon: <BarChart3 size={80} color="var(--purple)" />,
          title: 'Earnings History',
          description: 'Quarterly EPS actuals vs. consensus estimates. Track EPS data surprise percentage, absolute difference, and fiscal quarter end dates for the last 4-8 quarters.',
        },
        {
          fig: 'FIG 0.2',
          icon: <TrendingUp size={80} color="var(--green)" />,
          title: 'Forward Estimates',
          description: 'Current & next quarter EPS and revenue earnings estimates: average, low, high, analyst count, and year-over-year growth. Plus long-term growth (LTG).',
        },
        {
          fig: 'FIG 0.3',
          icon: <Calculator size={80} color="var(--red)" />,
          title: 'Estimate Revisions',
          description: 'Up/down revision counts over 7 and 30 days. EPS trend over 90 days to see momentum shifts before the print.',
        },
        {
          fig: 'FIG 0.4',
          icon: <FileText size={80} color="var(--purple)" />,
          title: 'Earnings Call Transcripts',
          description: 'Structured financial tables (balance sheet, cash flow, income) extracted directly from earnings releases, alongside earnings call API access. Scale multipliers and column headers preserved.',
        },
        {
          fig: 'FIG 0.5',
          icon: <LineChart size={80} color="var(--green)" />,
          title: 'Index Benchmark',
          description: 'S&P 500 earnings growth estimates for the same periods - benchmark company estimates against the broader market.',
        },
        {
          fig: 'FIG 0.6',
          icon: <Users size={80} color="var(--red)" />,
          title: 'Analyst Sentiment',
          description: 'Number of analysts covering the stock, consensus trend, and surprise histories to gauge market expectations and financial ratios.',
        },
      ]} />

      <Suspense fallback={null}>
      <GlobeComp
        eyebrow="Global Earnings Coverage"
        headingPlain="Earnings data"
        headingEm="for 60,000+ companies worldwide."
        body="Quarterly and annual earnings history, forward estimates, and earnings call transcripts from SEC, SEDAR, and global regulators. One earnings data API for US, Canada, UK, EU, and Asia-Pacific."
        stats={[
          { v: "60K+",   l: "Companies"       },
          { v: "10+ yr", l: "History"         },
          { v: "2M+",    l: "Quarterly filings" },
          { v: "24h",    l: "Daily Updates"   },
        ]}
        markers={[
          { id: "nyse",    location: [40.7069, -74.0113],  label: "NYSE"    },
          { id: "nasdaq",  location: [40.7560, -73.9857],  label: "NASDAQ"  },
          { id: "tsx",     location: [43.6487, -79.3803],  label: "TSX"     },
          { id: "lse",     location: [51.5155, -0.0993],   label: "LSE"     },
          { id: "euronext",location: [52.3731, 4.8952],    label: "Euronext"},
          { id: "deutsche",location: [50.1138, 8.6798],    label: "Xetra"   },
          { id: "six",     location: [47.3719, 8.5404],    label: "SIX"     },
          { id: "tse",     location: [35.6825, 139.7731],  label: "TSE"     },
          { id: "sse",     location: [31.2361, 121.5063],  label: "SSE"     },
          { id: "szse",    location: [22.5392, 114.0497],  label: "SZSE"    },
          { id: "hkex",    location: [22.2796, 114.1612],  label: "HKEX"    },
          { id: "krx",     location: [37.5226, 127.0453],  label: "KRX"     },
          { id: "bse",     location: [18.9292, 72.8339],   label: "BSE"     },
          { id: "asx",     location: [-33.8625, 151.2105], label: "ASX"     },
        ]}
      />

      <DataGrid
        title="Earnings Data API: From Filings to Forecasts"
        subtitle="Combine historical surprises, forward estimates, revision trends, earnings call transcripts, and granular 8-K tables. The complete earnings data API for quant strategies, fundamental analysis, and event-driven trading."
        bg={databg}
        stats={[
          { label: 'Quarterly Records', value: '2M+' },
          { label: 'Companies Covered', value: '60,000+' },
          { label: '8-K Tables Parsed', value: '500K+' },
        ]}
      />

      <CardList
        title="Engineered for Earnings Alpha"
        vid={'https://cdn.studio14a.com/axion/products/sc.mp4'}
        points={[
          {
            "title": "Actuals vs. Estimates",
            "details": "Quarterly EPS data actuals, consensus estimates, absolute difference, and surprise percentage. Filter by fiscal quarter and relative period (0q, -1q, etc.).",
            "cta": "Explore earnings history",
            "url": "/docs"
          },
          {
            "title": "Forward-Looking Estimates",
            "details": "Earnings estimates for EPS and revenue across current quarter, next quarter, current year, next year, and long-term growth. Plus trend data (7d/30d/60d/90d) and revision counts.",
            "cta": "View earnings trend",
            "url": "/products/stocks"
          },
          {
            "title": "Earnings Call Transcripts & SEC 8-K",
            "details": "Programmatic access to balance sheets, cash flow statements, income statements, and earnings call transcripts from earnings releases. Multiply values by scale factor for real numbers.",
            "cta": "Parse earnings reports",
            "url": "/products/disclosures"
          }
        ]}
      />

      <Responses
        heading="Real earnings numbers from real filings, across decades"
        subheading="Every endpoint returns data sourced directly from analyst consensus and SEC filings - no mock data, no placeholders. Here's exactly what you get back for Apple."
        ticker="AAPL"
        tabs={[
          {
            title: 'Earnings Calls Transcripts',
            description: 'Access full quarterly earnings call transcripts with sentiment analysis.',
            data: {
              "ticker": "AAPL",
              "year": "2024",
              "quarter": "2",
              "id": "eyJ0aWNrZXIiOiJBQVBMIiwieWVhciI6IjIwMjQiLCJxdWFydGVyIjoiMiJ9",
              "transcript": [
                {
                  "paragraphNumber": 1,
                  "speaker": "Suhasini Chandramouli",
                  "content": "Good Afternoon, and welcome to the Apple Q2 Fiscal Year 2024 Earnings Conference Call. My name is Suhasini Chandramouli, Director of Investor Relations. Today's call is being recorded. Speaking first today is Apple's CEO, Tim Cook, and he'll be followed by CFO, Luca Maestri. After that, we'll open the call to questions from analysts. Please note that some of the information you'll hear during our discussion today will consist of forward-looking statements, including, without limitation, those regarding revenue, gross margin, operating expenses, other income and expense, taxes, capital allocation and future business outlook, including the potential impact of macroeconomic conditions on the company's business and results of operations. These statements involve risks and uncertainties that may cause actual results or trends to differ materially from our forecast. For more information, please refer to the risk factors discussed in Apple's most recently filed Annual Report on Form 10-K and the Form 8-K filed with the SEC today, along with the associated press release. Apple assumes no obligation to update any forward-looking statements, which speak only as of the date they are made. I'd now like to turn the call over to Tim for introductory remarks."
                },
                {
                  "paragraphNumber": 2,
                  "speaker": "Tim Cook",
                  "content": "Thank you, Suhasini. Good afternoon, everyone, and thanks for joining the call. Today, Apple is reporting revenue of $90.8 billion and an EPS record of $1.53 for the March quarter. We set revenue records in more than a dozen countries and regions. These include, among others, March quarter records in Latin-America and the Middle East, as well as Canada, India, Spain and Turkey. We also achieved an all-time revenue record in Indonesia, one of the many markets where we continue to see so much potential. In services, we set an all-time revenue record, up 14% over the past year. Keep in mind, as we described on the last call, in the March quarter a year-ago, we were able to replenish iPhone channel inventory and fulfill significant pent-up demand from the December quarter COVID-related supply disruptions on the iPhone 14 Pro and 14 Pro Max. We estimate this one-time impact added close to $5 billion to the March quarter revenue last year. If we remove this from last year's results, our March quarter total company revenue this year would have grown. Despite this impact, we were still able to deliver the records I described. Of course, this past quarter, we were thrilled to launch Apple Vision Pro and it has been so wonderful to hear from people who now get to experience the magic of spatial computing. They describe the impossible becoming possible right before their eyes and they share their amazement and their emotions about what they can do now, whether it's reliving their most treasured memories or having a movie theater experience right in their living room. It's also great to see the enthusiasm from the enterprise market. For example, more than half of the Fortune 100 companies have already bought Apple Vision Pro units and are exploring innovative ways to use it to do things that weren't possible before, and this is just the beginning. Looking ahead, we're getting ready for an exciting product announcement next week that we think our customers will love. And next month, we have our Worldwide Developers Conference, which has generated enormous enthusiasm from our developers. We can't wait to reveal what we have in-store. We continue to feel very bullish about our opportunity in Generative AI. We are making significant investments, and we're looking forward to sharing some very exciting things with our customers soon. We believe in the transformative power and promise of AI, and we believe we have advantages that will differentiate us in this new era, including Apple's unique combination of seamless hardware, software and services integration, groundbreaking Apple's silicon, with our industry-leading neural engines and our unwavering focus on privacy, which underpins everything we create. As we push innovation forward, we continue to manage thoughtfully and deliberately through an uneven macroeconomic environment and remain focused on putting our users at the center of everything we do. Now let's turn to our results for the March quarter across each product category, beginning with iPhone. iPhone revenue for the March quarter was $46 billion, down 10% year-over-year. We faced a difficult compare over the previous year due to the $5 billion impact that I mentioned earlier. However, we still saw growth on iPhone in some markets, including Mainland China, and according to Kantar during the quarter, the two best-selling smartphones in Urban China were the iPhone 15 and iPhone 15 Pro Max. I was in China recently where I had the chance to meet with developers and creators who are doing remarkable things with iPhone. And just a couple of weeks ago, I visited Vietnam, Indonesia and Singapore, where it was incredible to see all the ways customers and communities are using our products and services to do amazing things. Everywhere I travel, people have such a great affinity for Apple, and it's one of the many reasons I'm so optimistic about the future. Turning to Mac. March quarter revenue was $7.5 billion, up 4% from a year ago. We had an amazing launch in early March with the new 13-inch and 15-inch MacBook Air. The world's most popular laptop is the best consumer laptop for AI with breakthrough performance of the M3 chip and it's even more powerful neural engine. Whether it's an entrepreneur starting a new business or a college student finishing their degree, users depend on the power and portability of MacBook Air to take them places they couldn't have gone without it. In iPad, revenue for the March quarter was $5.6 billion, 17% lower year-over-year, due to a difficult compare with the momentum following the launch of M2 iPad Pro and the 10th Generation iPad last fiscal year. iPad continues to stand apart for its versatility, power and performance. For video editors, music makers and creatives of all kinds, iPad is empowering users to do more than they ever could with a tablet. Across Wearables, Home and Accessories, March quarter revenue was $7.9 billion, down 10% from a year-ago due to a difficult launch compare on Watch and AirPods. Apple Watch is helping runners go the extra mile on their wellness journeys, keeping hikers on course with the latest navigation capabilities in watchOS 10, and enabling users of all fitness levels to live a healthier day. Across our watch lineup, we're harnessing AI and machine-learning to power lifesaving features like a regular rhythm notifications and fall detection. I often hear about how much these features mean to users and their loved ones and I'm thankful that so many people are able to get help in their time of greatest need. As I shared earlier, we set an all-time revenue record in services with $23.9 billion, up 14% year-over-year. We also achieved all-time revenue records across several categories and geographic segments. Audiences are tuning in on screens large, small and spatial and are enjoying Apple TV+ Originals like Palm Royale and Sugar. And we have some incredible theatrical releases coming this year, including Wolves, which reunites George Clooney and Brad Pitt. Apple TV+ productions continue to be celebrated as major awards contenders. Since launch, Apple TV+ productions have earned more than 2,100 award nominations and 480 wins. Meanwhile, we're enhancing the live sports experience with a new iPhone app, Apple Sports. This free app allows fans to follow their favorite teams and leagues with real-time scores, stats and more. Apple Sports is the perfect companion for MLS Season Pass subscribers. Turning to retail, our stores continued to be vital spaces for connection and innovation. I was delighted to be in Shanghai for the opening of our latest flagship store. The energy and enthusiasm from our customers was truly something to behold. And across the United States, our incredible retail teams have been sharing Vision Pro demos with customers, delighting them with a profound and emotional experience of using it for the very first time. Everywhere we operate and everything we do, we're guided by our mission to enrich users' lives and lead the world better than we found it, whether we're making Apple podcasts more accessible with a new transcripts feature or helping to safeguard iMessage users' privacy with new protections that can defend against advances in quantum computing. Our environmental work is another great example of how innovation and our values come together. As we work toward our goal of being carbon-neutral across all of our products by 2030, we are proud of how we've been able to innovate and do more for our customers while taking less from the planet. Since 2015, Apple has cut our overall emissions by more than half, while revenue grew nearly 65% during that same time period. And we're now using more recycled materials in our products than ever before. Earlier this spring, we launched our first-ever product to use 50% recycled materials with a new M3-powered MacBook Air. We're also investing in new solar and wind power in the U.S. and Europe, both to power our growing operations and our users' devices. And we're working with partners in India and the U.S. to replenish 100% of the water we use in places that need it most with the goal of delivering billions of gallons of water benefits over the next two decades. Through our Restore Fund, Apple has committed $200 million to nature-based carbon removal projects. And last month, we welcomed two supplier partners as new investors, who will together invest up to an additional $80 million in the fund. Whether we're enriching lives of users across the globe or doing our part to be a force for good in the world, we do everything with a deep sense of purpose at Apple. And I'm proud of the impact we've already made at the halfway point in a year of unprecedented innovation. I couldn't be more excited for the future we have ahead of us, driven by the imagination and innovation of our teams and the enduring importance of our products and services in people's lives. With that, I'll turn it over to Luca."
                },
                {
                  "paragraphNumber": 3,
                  "speaker": "Luca Maestri",
                  "content": "Thank you, Tim, and good afternoon, everyone. Revenue for the March quarter was $90.8 billion, down 4% from last year. Foreign exchange had a negative year-over-year impact of 140 basis points on our results. Products revenue was $66.9 billion, down 10% year-over-year due to the challenging compare on iPhone that Tim described earlier, which was partially offset by strength from Mac. And thanks to our unparalleled customer satisfaction and loyalty and a high number of customers who are new to our products, our installed base of active devices reached an all-time high across all products and all geographic segments. Services revenue set an all-time record of $23.9 billion, up 14% year-over-year with record performance in both developed and emerging markets. Company gross margin was 46.6%, up 70 basis points sequentially, driven by cost savings and favorable mix to services, partially offset by leverage. Products gross margin was 36.6%, down 280 basis points sequentially, primarily driven by seasonal loss of leverage and mix, partially offset by favorable costs. Services gross margin was 74.6%, up 180 basis points from last quarter due to a more favorable mix. Operating expenses of $14.4 billion were at the midpoint of the guidance range we provided and up 5% year-over-year. Net income was $23.6 billion, diluted EPS was $1.53 and a March quarter record, and operating cash flow was strong at $22.7 billion. Let me now provide more detail for each of our revenue categories. iPhone revenue was $46 billion, down 10% year-over-year, due to the almost $5 billion impact from a year ago that Tim described earlier. Adjusting for this one-time impact, iPhone revenue would be roughly flat to last year. Our iPhone active installed base grew to a new all-time high in total and in every geographic segment. And during the March quarter, we saw many iPhone models as the top-selling smartphones around the world. In fact, according to a survey from Kantar, an iPhone was the top-selling model in the U.S., Urban China, Australia, the U.K., France, Germany and Japan. And the iPhone 15 family continues to be very popular with customers. 451 Research recently measured customer satisfaction at 99% in the U.S. Mac revenue was $7.5 billion, up 4% year-over-year, driven by the strength of our new MacBook Air, powered by the M3 chip. Customers are loving the incredible AI performance of the latest MacBook Air and MacBook Pro models. And our Mac installed base reached an all-time high with half of our MacBook Air buyers during the quarter being new to Mac. Also customer satisfaction for Mac was recently reported at 96% in the U.S. iPad generated $5.6 billion in revenue, down 17% year-over-year. iPad continued to face a challenging compare against the launch of the M2 iPad Pro and iPad 10th Generation from last year. At the same time, the iPad installed base has continued to grow and is at an all-time high as over half of the customers who purchased iPads during the quarter were new to the product. In addition, the latest reports from 451 Research indicated customer satisfaction of 96% for iPad in the US. Wearables, Home and Accessories revenue was $7.9 billion, down 10% year-over-year due to a difficult launch compare. Last year, we had the continued benefit from the launches of the AirPods Pro second-generation, the Watch SE and the first Watch Ultra. Apple Watch continues to attract new customers, with almost two-thirds of customers purchasing an Apple Watch during the quarter being new to the product, sending the Apple Watch installed base to a new all-time high and customer satisfaction was recently measured at 95% in the U.S. In services, as I mentioned, total revenue reached an all-time record of $23.9 billion, growing 14% year-over-year with our installed-base of active devices continuing to grow at a nice pace. This provides a strong foundation for the future growth of the services business as we continued to see increased customer engagement with our ecosystem. Both transacting accounts and paid accounts reached a new all-time high with paid accounts growing double-digits year-over-year. And paid subscriptions showed strong double-digit growth. We have well over $1 billion paid subscriptions across the services on our platform, more than double the number that we had only four years ago. We continued to improve the breadth and quality of our current services from creating new games on Arcade and great new shows on TV+ to launching additional countries and partners for Apple Pay. Turning to enterprise, our customers continued to invest in Apple products to drive productivity and innovation. We see more and more enterprise customers embracing the Mac. In Healthcare, Epic Systems, the world's largest electronic medical record provider, recently launched its native app for the Mac, making it easier for healthcare organizations like Emory Health to transition thousands of PCs to the Mac for clinical use. And since the launch of Vision Pro last quarter, many leading enterprise customers have been investing in this amazing new product to bring spatial computing apps and experiences to life. We are seeing so many compelling use cases from aircraft engine maintenance training at KLM Airlines to real-time team collaboration for racing at Porsche to immersive kitchen design at Lowe's. We couldn't be more excited about the spatial computing opportunity in enterprise. Taking a quick step back, when we look at our performance during the first-half of our fiscal year, total company revenue was roughly flat to the prior year in spite of having one less week of sales during the period and some foreign exchange headwinds. We were particularly pleased with our strong momentum in emerging markets, as we set first-half revenue records in several countries and regions, including Latin-America, the Middle East, India, Indonesia, the Philippines and Turkey. These results, coupled with double-digit growth in services and strong levels of gross margin, drove a first half diluted EPS record of $3.71, up 9% from last year. Let me now turn to our cash position and capital return program. We ended the quarter with $162 billion in cash and marketable securities. We repaid $3.2 billion in maturing debt and commercial paper was unchanged sequentially, leaving us with total debt of $105 billion. As a result, net cash was $58 billion at the end of the quarter. During the quarter, we returned over $27 billion to shareholders, including $3.7 billion in dividends and equivalents and $23.5 billion through open-market repurchases of $130 million Apple's shares. Given the continued confidence we have in our business now and into the future, our Board has authorized today an additional $110 billion for share repurchases, as we maintain our goal of getting to net cash-neutral over time. We are also raising our dividend by 4% to $0.25 per share of common stock, and we continued to plan for annual increases in the dividend going forward as we've done for the last 12 years. This cash dividend will be payable on May 16, 2024 to shareholders of record as of May 13, 2024. As we move ahead into the June quarter, I'd like to review our outlook, which includes the types of forward-looking information that Suhasini referred to at the beginning of the call. The color we are providing today assumes that the macroeconomic outlook doesn't worsen from what we are projecting today for the current quarter. We expect our June quarter total company revenue to grow low-single-digits year-over-year in spite of a foreign exchange headwind of about 2.5 percentage points. We expect our services business to grow double-digits at a rate similar to the growth we reported for the first-half of the fiscal year. And we expect iPad revenue to grow double-digits. We expect gross margin to be between 45.5% to -- and 46.5%. We expect OpEx to be between $14.3 billion and $14.5 billion. We expect OI&E to be around $50 million, excluding any potential impact from the mark-to-market of minority investments and our tax rate to be around 16%. With that, let's open the call to questions."
                },
                {
                  "paragraphNumber": 4,
                  "speaker": "Suhasini Chandramouli",
                  "content": "Thank you, Luca. We ask that you limit yourself to two questions. Operator, may we have the first question, please?"
                },
                {
                  "paragraphNumber": 5,
                  "speaker": "Operator",
                  "content": "Certainly. We will go ahead and take our first question from Mike Ng with Goldman Sachs. Please go ahead."
                },
                {
                  "paragraphNumber": 6,
                  "speaker": "Mike Ng",
                  "content": "Hey, good afternoon. Thank you very much for the question. I have two, first, I'll ask about the June quarter guidance. The revenue outlook for low-single digits growth, I was wondering if you could run through some of the product assumptions, iPhone, like what kind of gives you confidence around that? And then on the service momentum, what was better than expected in the quarter? And then I just have a quick follow-up."
                },
                {
                  "paragraphNumber": 7,
                  "speaker": "Luca Maestri",
                  "content": "Hey, Mike. It's Luca. On the outlook, what we said is we expect to grow low-single-digits in total for the company. We expect services to grow double-digits at a rate that is similar to what we've done in the first-half of our fiscal year. And we've also mentioned that iPad should grow double-digits. This is the color that we're providing for the June quarter. In services, we've seen a very strong performance across the board. We've mentioned, we've had records in several categories, in several geographic segments. It's very broad based, our subscription business is going well. Transacting accounts and paid accounts are growing double-digits. And also we've seen a really strong performance both in developed and emerging markets. So very pleased with the way the services business is going."
                },
                {
                  "paragraphNumber": 8,
                  "speaker": "Mike Ng",
                  "content": "Great. Thank you. And I wanted to ask about, as Apple leans more into AI and Generative AI, should we expect any changes to the historical CapEx cadence that we've seen in the last few years of about $10 billion to $11 billion per year or any changes to, you know, how we may have historically thought about the split between tooling, data center and facilities? Thank you very much."
                },
                {
                  "paragraphNumber": 9,
                  "speaker": "Luca Maestri",
                  "content": "Yes. We are obviously very excited about the opportunity with Gen AI. We obviously are pushing very hard on innovation on every front and we've been doing that for many, many years. Just during the last five years, we spent more than a $100 billion in research and development. As you know, on the CapEx front, we have a bit of a hybrid model where we make some of the investments ourselves. In other cases, we share them with our suppliers and partners on the manufacturing side, we purchased some of the tools and manufacturing equipment. In some of the cases, our suppliers make the investment. On the -- and we do something similar on the data center side. We have our own data center capacity and then we use capacity from third parties. It's a model that has worked well for us historically and we plan to continue along the same lines going forward."
                },
                {
                  "paragraphNumber": 10,
                  "speaker": "Mike Ng",
                  "content": "Excellent. Thank you very much."
                },
                {
                  "paragraphNumber": 11,
                  "speaker": "Suhasini Chandramouli",
                  "content": "Awesome. Thank you, Mike. Operator, can we have the next question, please?"
                },
                {
                  "paragraphNumber": 12,
                  "speaker": "Operator",
                  "content": "Our next question is from Wamsi Mohan with Bank of America. Please go ahead."
                }
              ]
            }
          },

          {
            title: 'Earnings History',
            description: 'Actual vs. estimated EPS for the last several quarters, including surprise percentage and the exact fiscal quarter each figure belongs to.',
            data: [
              {
                epsActual: 2.4,
                epsEstimate: 2.34102,
                epsDifference: 0.06,
                surprisePercent: 0.0252,
                quarter: '2024-12-31T00:00:00.000Z',
                currency: 'USD',
                period: '-4q',
              },
              {
                epsActual: 1.65,
                epsEstimate: 1.62253,
                epsDifference: 0.03,
                surprisePercent: 0.016900001,
                quarter: '2025-03-31T00:00:00.000Z',
                currency: 'USD',
                period: '-3q',
              },
            ],
          },
          {
            title: 'Earnings Calendar',
            description: 'Track upcoming earnings releases with analyst EPS and revenue estimates, report dates, and the scheduled market session for each announcement.',
            data: {
              "earnings": {
                "earningsDate": ["2026-01-29T21:00:00.000Z"],
                "earningsCallDate": ["2025-10-30T21:00:00.000Z"],
                "isEarningsDateEstimate": true,
                "earningsAverage": 2.6647,
                "earningsLow": 2.51,
                "earningsHigh": 2.76,
                "revenueAverage": 138253076640,
                "revenueLow": 136679500000,
                "revenueHigh": 142741000000
              },
              "exDividendDate": "2025-11-10T00:00:00.000Z",
              "dividendDate": "2025-11-13T00:00:00.000Z"
            }
            ,
          },

          {
            title: 'Earnings Trend',
            description: 'Forward-looking EPS and revenue estimates for the current quarter, including analyst growth projections, year-ago comparisons, and recent estimate revisions.',
            data: [
              {
                period: '0q',
                endDate: '2025-12-31T00:00:00.000Z',
                growth: 0.1103,
                earningsEstimate: {
                  avg: 2.66574,
                  low: 2.51,
                  high: 2.8,
                  yearAgoEps: 2.4,
                  numberOfAnalysts: 29,
                  growth: 0.1107,
                },
                revenueEstimate: {
                  avg: 138253076640,
                  low: 136679500000,
                  high: 142741000000,
                  numberOfAnalysts: 29,
                  yearAgoRevenue: 124300000000,
                  growth: 0.1123,
                },
                epsTrend: {
                  current: 2.66574,
                  '7daysAgo': 2.66367,
                  '30daysAgo': 2.66153,
                  '60daysAgo': 2.48232,
                  '90daysAgo': 2.492,
                },
                epsRevisions: {
                  upLast7days: 1,
                  upLast30days: 1,
                  downLast30days: 0,
                  downLast7Days: 0,
                },
              },
            ],
          },
          {
            title: 'Index Trend (S&P 500)',
            description: 'Earnings growth estimates for the S&P 500, so you can see whether a company is outpacing or lagging the broader market.',
            data: {
              symbol: 'SP5',
              estimates: [
                { period: '0q', growth: 0.1524 },
                { period: '+1q', growth: 0.070300005 },
                { period: '0y', growth: 0.109 },
                { period: '+1y', growth: 0.1487 },
                { period: 'LTG', growth: 0.122 },
              ],
            },
          },
          {
            title: 'Earnings Report (8-K)',
            description: 'Structured financial statements parsed directly from the official SEC filing - balance sheet, cash flow, and more, ready to drop into a table.',
            data: [
              {
                dataframe: [
                  { label: 'December 30, 2023', values: ['September 30, 2023'] },
                  { label: 'ASSETS:', values: [] },
                  { label: 'Current assets:', values: [] },
                  { label: 'Cash and cash equivalents', values: ['$', 40760, '$', 29965] },
                  { label: 'Marketable securities', values: [32340, 31590] },
                  { label: 'Total current assets', values: [143692, 143566] },
                  { label: 'Total assets', values: ['$', 353514, '$', 352583] },
                ],
                scale: 1000000000,
                title: null,
                statementType: 'balance_sheet',
                periods: ['Period 1'],
                rawIndex: 5,
              },
              {
                dataframe: [
                  { label: 'Three Months Ended', values: [] },
                  { label: 'December 30, 2023', values: ['December 31, 2022'] },
                  { label: 'Cash generated by operating activities', values: [39895, 34005] },
                  { label: 'Cash generated by/(used in) investing activities', values: [1927, -1445] },
                  { label: 'Cash used in financing activities', values: [-30585, -35563] },
                ],
                scale: 1000000000,
                title: null,
                statementType: 'cash_flow',
                periods: [],
                rawIndex: 6,
              },
            ],
          },
        ]}
      />
      <DatasetDetails full={true}
        title="Earnings Data Coverage, Institutional Precision"
        subtitle="Access historical and forward earnings data for 60,000+ public companies worldwide. Get quarterly EPS actuals vs estimates, surprise percentages, analyst revision trends, earnings call transcripts, and parsed 8-K tables - all through a single earnings data API. Built for earnings backtesting, event-driven trading, and fundamental analysis."
        cta="Access Now"
        ctaLink="/login"
        tiles={[
          {
            img: '0.webp',
            title: 'We cover 60,000+ companies',
            caption: 'You can access historical and forward earnings data for public companies worldwide.',
            cn: 'scaled',
            tile: <W1/>
          },
          {
            img: '1.webp',
            title: 'We maintain 2M+ quarterly records',
            caption: 'You can analyze EPS actuals, estimates, and surprise percentages across decades of earnings data.',
            cn: 'scaled',
            tile: <W2/>
          },
          {
            img: '6.webp',
            title: 'We provide 10+ years of history',
            caption: 'You can backtest earnings strategies and model earnings reactions across full market cycles.',
            cn: 'scaled',
            tile: <W3/>
          },
          {
            img: '9.webp',
            title: 'We track 25 analysts per stock on average',
            caption: 'You get comprehensive consensus earnings estimates and revision trends for deeper analysis.',
            cn: 'scaled',
            tile: <W4/>
          },
          {
            img: '4.webp',
            title: 'We deliver data in under 24 hours',
            caption: 'You receive structured earnings data within a day of SEC filing for timely analysis.',
            cn: 'scaled',
            tile: <W5/>
          }
        ]}
      />

      <Outcomes
        headline="Numbers that speak for themselves."
        subheadline="Institutional-grade earnings data API trusted by quant funds, fundamental investors, and fintech platforms."
        leftCell={{ stat: '2M+', title: '2M+ quarterly records.', desc: 'Historical EPS actuals and estimates across 60K+ companies.', showChart: true }}
        rightCells={[
          { stat: '24h', title: 'Daily Updates.', desc: 'Fresh estimates and filing data within 24 hours.' },
          { stat: '10+YR', title: '10+ Years of History.', desc: 'Backtest earnings strategies across full market cycles.', boldDesc: true },
        ]}
      /><ProductFAQ slug="earnings" />


      <PricingTable />
      <GetStarted />

      <Footer />
      </Suspense>
      </StyledProducts>
    </>
  );
};

export default EarningsPage;
